import { emitChatContextTelemetry } from './chatContextTelemetry';
import { zendeskMessagingAdapter } from './zendeskMessaging';
import type {
  ZendeskConversationField,
  ZendeskSupportContextAdapter,
  ZendeskSupportScalar
} from './zendeskSupportContextTypes';

export type {
  ZendeskConversationField,
  ZendeskOutgoingMessage,
  ZendeskSupportContextAdapter,
  ZendeskSupportScalar
} from './zendeskSupportContextTypes';

export type IokSupportIdentityMode =
  | 'authenticated'
  | 'iok_user_zendesk_anonymous'
  | 'public_anonymous';

export type IokSupportEntryPoint =
  | 'desktop_nav'
  | 'mobile_tab'
  | 'chat_page'
  | 'panel'
  | 'minimized_control';

export type IokSupportModule =
  | 'home'
  | 'cookbook'
  | 'menus'
  | 'equipment'
  | 'users'
  | 'iok_information'
  | 'administration'
  | 'chat'
  | 'other';

export interface IokSupportContext {
  identityMode: IokSupportIdentityMode;
  userId?: number;
  companyId?: number;
  userRole?: string;
  locale: string;
  environment: 'prod' | 'test' | 'local';
  entryPoint: IokSupportEntryPoint;
  initialModule: IokSupportModule;
  currentModule: IokSupportModule;
  currentPath: string;
}

export interface ZendeskSupportFieldIds {
  userId?: string;
  companyId?: string;
  userRole?: string;
  locale?: string;
  environment?: string;
  entryPoint?: string;
  initialModule?: string;
  identityMode?: string;
}

export const isZendeskMessagingContextEnabled =
  process.env.REACT_APP_ZENDESK_MESSAGING_CONTEXT_ENABLED === 'true';

export const zendeskSupportFieldIds: ZendeskSupportFieldIds = {
  userId: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_USER_ID_FIELD_ID,
  companyId: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_COMPANY_ID_FIELD_ID,
  userRole: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_USER_ROLE_FIELD_ID,
  locale: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_LOCALE_FIELD_ID,
  environment: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_ENVIRONMENT_FIELD_ID,
  entryPoint: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_ENTRY_POINT_FIELD_ID,
  initialModule: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_INITIAL_MODULE_FIELD_ID,
  identityMode: process.env.REACT_APP_ZENDESK_MESSAGING_IOK_IDENTITY_MODE_FIELD_ID
};

const CONTEXT_SCHEMA_VERSION = 1;
const DEFAULT_TIMEOUT_MS = 2000;

export const normalizePositiveId = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;

export const normalizeUserRole = (value: unknown): string => {
  if (typeof value !== 'string') return 'unknown';
  const normalized = value.trim().toLowerCase();
  const knownRoles: Record<string, string> = {
    adm: 'administrator',
    administrator: 'administrator',
    administrador: 'administrator',
    user: 'user',
    usuario: 'user',
    usuário: 'user',
    usercookbook: 'cookbook_user',
    userequipment: 'equipment_user',
    usermenu: 'menu_user',
    operator: 'operator',
    operador: 'operator'
  };
  return knownRoles[normalized] ?? 'unknown';
};

export const getIokSupportEnvironment = (
  nodeEnvironment: string | undefined =
    process.env.REACT_APP_ZENDESK_MESSAGING_CONTEXT_ENVIRONMENT ?? process.env.NODE_ENV
): 'prod' | 'test' | 'local' => {
  if (nodeEnvironment === 'prod' || nodeEnvironment === 'production') return 'prod';
  if (nodeEnvironment === 'homol' || nodeEnvironment === 'test') return 'test';
  return 'local';
};

export const classifyIokSupportPath = (
  pathname: string
): { module: IokSupportModule; path: string } => {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (/^\/home(?:\/|$)/.test(path)) return { module: 'home', path: '/home' };
  if (/^\/cookbook(?:\/|$)/.test(path)) return { module: 'cookbook', path: '/cookbook' };
  if (/^\/menu(?:\/|$)/.test(path)) return { module: 'menus', path: '/menu' };
  if (/^\/equipment(?:\/|$)/.test(path)) return { module: 'equipment', path: '/equipment' };
  if (/^\/users(?:\/|$)/.test(path)) return { module: 'users', path: '/users' };
  if (/^\/information(?:\/|$)/.test(path)) {
    return { module: 'iok_information', path: '/information' };
  }
  if (/^\/chat(?:\/|$)/.test(path)) return { module: 'chat', path: '/chat' };
  if (/^\/(analytics|calls|companies|companyMerge|entity)(?:\/|$)/.test(path)) {
    return { module: 'administration', path: '/administration' };
  }
  return { module: 'other', path: '/other' };
};

const configuredField = (
  id: string | undefined,
  value: ZendeskSupportScalar | undefined
): ZendeskConversationField | undefined => {
  const normalizedId = id?.trim();
  if (!normalizedId || value === undefined) return undefined;
  return { id: normalizedId, value };
};

export const buildZendeskSupportPayload = (
  context: IokSupportContext,
  fieldIds: ZendeskSupportFieldIds = zendeskSupportFieldIds
) => {
  const includesIdentity = context.identityMode !== 'public_anonymous';
  const fieldCandidates = [
    configuredField(fieldIds.userId, includesIdentity ? context.userId : undefined),
    configuredField(fieldIds.companyId, includesIdentity ? context.companyId : undefined),
    configuredField(fieldIds.userRole, includesIdentity ? context.userRole : undefined),
    configuredField(fieldIds.locale, context.locale),
    configuredField(fieldIds.environment, context.environment),
    configuredField(fieldIds.entryPoint, context.entryPoint),
    configuredField(fieldIds.initialModule, context.initialModule),
    configuredField(fieldIds.identityMode, context.identityMode)
  ];
  const fields = fieldCandidates.filter(
    (field): field is ZendeskConversationField => Boolean(field)
  );
  const identityTag = context.identityMode === 'authenticated'
    ? 'iok_authenticated'
    : context.identityMode === 'iok_user_zendesk_anonymous'
      ? 'iok_zendesk_anonymous'
      : 'iok_public_anonymous';
  const tags = [
    'iok_messaging',
    identityTag,
    `iok_env_${context.environment}`,
    `iok_entry_${context.entryPoint}`,
    `iok_module_${context.initialModule}`
  ];
  const metadata: Record<string, ZendeskSupportScalar> = {
    iok_context_schema_version: CONTEXT_SCHEMA_VERSION,
    iok_identity_mode: context.identityMode,
    iok_authenticated: context.identityMode === 'authenticated',
    iok_locale: context.locale,
    iok_environment: context.environment,
    iok_entry_point: context.entryPoint,
    iok_initial_module: context.initialModule
  };
  if (includesIdentity) {
    if (context.userId !== undefined) metadata.iok_user_id = context.userId;
    if (context.companyId !== undefined) metadata.iok_company_id = context.companyId;
    metadata.iok_user_role = context.userRole ?? 'unknown';
  }
  return { fields, tags: Array.from(new Set(tags)), metadata };
};

export class ZendeskSupportContextManager {
  private generation = 0;
  private appliedKey?: string;
  private pendingKey?: string;
  private pending?: Promise<void>;
  private writeQueue: Promise<void> = Promise.resolve();
  private unsubscribeBeforeMessageSent?: () => void;
  private snapshot?: Pick<IokSupportContext, 'currentModule' | 'currentPath' | 'entryPoint'>;

  constructor(
    private readonly adapter: ZendeskSupportContextAdapter,
    private readonly enabled = isZendeskMessagingContextEnabled,
    private readonly fieldIds: ZendeskSupportFieldIds = zendeskSupportFieldIds,
    private readonly timeoutMs = DEFAULT_TIMEOUT_MS
  ) {}

  apply(context: IokSupportContext): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    const payload = buildZendeskSupportPayload(context, this.fieldIds);
    const key = JSON.stringify({ context, payload });
    this.snapshot = {
      currentModule: context.currentModule,
      currentPath: context.currentPath,
      entryPoint: context.entryPoint
    };
    let delegateFailure: unknown;
    try {
      this.ensureBeforeMessageSent();
    } catch (error) {
      delegateFailure = error;
    }
    if (key === this.appliedKey) return Promise.resolve();
    if (key === this.pendingKey && this.pending) return this.pending;

    const generation = ++this.generation;
    this.pendingKey = key;
    const run = async () => {
      if (generation !== this.generation) return;
      let failure: unknown = delegateFailure;
      try {
        await this.adapter.setConversationFields(payload.fields, this.timeoutMs);
      } catch (error) {
        failure = error;
      }
      if (generation !== this.generation) return;
      try {
        this.adapter.setConversationTags(payload.tags);
      } catch (error) {
        failure ??= error;
      }
      if (generation !== this.generation) return;
      try {
        await this.adapter.setConversationMetadata(payload.metadata, this.timeoutMs);
      } catch (error) {
        failure ??= error;
      }
      if (generation !== this.generation) return;

      if (!failure) {
        this.appliedKey = key;
        emitChatContextTelemetry({
          name: 'chat_context_applied',
          identity_mode: context.identityMode,
          keys: Object.keys(payload.metadata).sort()
        });
      } else {
        emitChatContextTelemetry({
          name: 'chat_context_apply_failed',
          identity_mode: context.identityMode,
          keys: Object.keys(payload.metadata).sort(),
          error: failure instanceof Error && failure.message === 'timeout' ? 'timeout' : 'sdk-error'
        });
      }
    };
    const queued = this.writeQueue.catch(() => undefined).then(run);
    let operation: Promise<void>;
    operation = queued.finally(() => {
      if (this.pending === operation) {
        this.pendingKey = undefined;
        this.pending = undefined;
      }
    });
    this.writeQueue = operation.catch(() => undefined);
    this.pending = operation;
    return operation;
  }

  updateNavigation(currentModule: IokSupportModule, currentPath: string): void {
    if (!this.enabled || !this.snapshot) return;
    this.snapshot = { ...this.snapshot, currentModule, currentPath };
  }

  clear(): void {
    this.generation += 1;
    this.appliedKey = undefined;
    this.pendingKey = undefined;
    this.pending = undefined;
    this.snapshot = undefined;
    const unsubscribe = this.unsubscribeBeforeMessageSent;
    this.unsubscribeBeforeMessageSent = undefined;
    try {
      unsubscribe?.();
    } catch {
      // A generation change keeps any late callback inert.
    }
  }

  private ensureBeforeMessageSent(): void {
    if (this.unsubscribeBeforeMessageSent) return;
    this.unsubscribeBeforeMessageSent = this.adapter.setBeforeMessageSent((message) => {
      const snapshot = this.snapshot;
      if (!snapshot) return message;
      return {
        ...message,
        metadata: {
          ...(message.metadata ?? {}),
          iok_context_schema_version: CONTEXT_SCHEMA_VERSION,
          iok_current_module: snapshot.currentModule,
          iok_current_path: snapshot.currentPath,
          iok_entry_point: snapshot.entryPoint
        }
      };
    });
  }
}

export const zendeskSupportContextManager = new ZendeskSupportContextManager(
  zendeskMessagingAdapter
);
